import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarClock, CheckCircle2, Loader2, Plus, RotateCcw, XCircle } from "lucide-react";
import { formatApiError } from "./AuthContext";
import { TaskDuePill } from "./TasksSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-1";

export default function TasksPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Open");
  const [records, setRecords] = useState({ leads: [], clients: [] });
  const [form, setForm] = useState({ title: "", dueDate: "", related: "", ownerEmail: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/tasks`);
      setData(r.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load linkable records for the create form (either fetch may 403 by module access)
  useEffect(() => {
    (async () => {
      const out = { leads: [], clients: [] };
      try {
        const r = await axios.get(`${API}/admin/leads`, { params: { limit: 200 } });
        out.leads = r.data.leads.map((l) => ({
          id: l.id,
          label: `${l.businessName || `${l.firstName} ${l.lastName}`} (${l.sourceLabel})`,
        }));
      } catch (_) {}
      try {
        const r = await axios.get(`${API}/admin/clients`);
        out.clients = r.data.clients.map((c) => ({
          id: c.id,
          label: c.businessName || `${c.contactFirstName} ${c.contactLastName}`,
        }));
      } catch (_) {}
      setRecords(out);
    })();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.dueDate || !form.related) return;
    const [relatedType, relatedId] = form.related.split("::");
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/admin/tasks`, {
        title: form.title.trim(),
        dueDate: form.dueDate,
        relatedType,
        relatedId,
        ...(form.ownerEmail ? { ownerEmail: form.ownerEmail } : {}),
      });
      setForm({ title: "", dueDate: "", related: "", ownerEmail: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (task, status) => {
    try {
      await axios.patch(`${API}/admin/tasks/${task.id}`, { status });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!data) {
    if (error) {
      return <div data-testid="tasks-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
    }
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading tasks…
      </div>
    );
  }

  const open = data.tasks.filter((t) => t.status === "Open");
  const overdue = open.filter((t) => t.isOverdue);
  const today = open.filter((t) => t.dueToday);
  const upcoming = open.filter((t) => !t.isOverdue && !t.dueToday);
  const closed = data.tasks.filter((t) => t.status !== "Open");
  const visible = tab === "Open" ? open : tab === "Done" ? closed.filter((t) => t.status === "Done") : data.tasks;

  const relatedLink = (t) => t.relatedType === "lead" ? `/admin/leads/${t.relatedId}` : `/admin/clients/${t.relatedId}`;

  return (
    <div data-testid="admin-tasks" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Tasks & reminders</h1>
        <p className="mt-2 text-mid-neutral">
          Follow-ups across leads and clients. Owners get an email on the due date.
        </p>
      </header>

      {data.dueCount > 0 && (
        <div data-testid="tasks-due-banner" className="rounded-2xl border border-yellow-300 bg-yellow-50 p-4 text-sm font-semibold text-forest">
          {data.dueCount} task{data.dueCount === 1 ? "" : "s"} due today or overdue.
        </div>
      )}

      {error && (
        <div data-testid="tasks-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Create */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <Plus className="h-4 w-4 text-mint" /> New follow-up task
        </h2>
        <form onSubmit={create} className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className={labelCls}>Task</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Call to follow up on proposal" data-testid="task-create-title" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Related to</label>
            <select required value={form.related} onChange={(e) => setForm({ ...form, related: e.target.value })}
              data-testid="task-create-related" className={inputCls}>
              <option value="">Select lead or client…</option>
              {records.leads.length > 0 && (
                <optgroup label="Leads">
                  {records.leads.map((l) => <option key={`lead-${l.id}`} value={`lead::${l.id}`}>{l.label}</option>)}
                </optgroup>
              )}
              {records.clients.length > 0 && (
                <optgroup label="Clients">
                  {records.clients.map((c) => <option key={`client-${c.id}`} value={`client::${c.id}`}>{c.label}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label className={labelCls}>Due date</label>
            <input required type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              data-testid="task-create-due" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Owner</label>
            <select value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
              data-testid="task-create-owner" className={inputCls}>
              <option value="">Me</option>
              {data.assignees.map((a) => <option key={a.id} value={a.email}>{a.name || a.email}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-5 flex justify-end">
            <button type="submit" disabled={!form.title.trim() || !form.dueDate || !form.related || saving}
              data-testid="task-create-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add task
            </button>
          </div>
        </form>
      </section>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        {["Open", "Done", "All"].map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`tasks-tab-${t.toLowerCase()}`}
            className={"rounded-lg px-4 py-2 text-sm font-bold transition-colors " +
              (tab === t ? "bg-forest text-lime" : "bg-white border border-border-neutral text-forest hover:bg-soft")}>
            {t}{t === "Open" ? ` (${open.length})` : ""}
          </button>
        ))}
      </div>

      {/* Grouped list */}
      {tab === "Open" ? (
        <div className="space-y-6">
          <TaskGroup title="Overdue" tasks={overdue} accent="text-red-700" onStatus={setStatus} relatedLink={relatedLink} testPrefix="overdue" />
          <TaskGroup title="Due today" tasks={today} accent="text-yellow-700" onStatus={setStatus} relatedLink={relatedLink} testPrefix="today" />
          <TaskGroup title="Upcoming" tasks={upcoming} accent="text-forest" onStatus={setStatus} relatedLink={relatedLink} testPrefix="upcoming" />
        </div>
      ) : (
        <TaskGroup title={tab === "Done" ? "Completed" : "All tasks"} tasks={visible} accent="text-forest" onStatus={setStatus} relatedLink={relatedLink} testPrefix="list" />
      )}
    </div>
  );
}

function TaskGroup({ title, tasks, accent, onStatus, relatedLink, testPrefix }) {
  return (
    <section data-testid={`tasks-group-${testPrefix}`}>
      <h2 className={"flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.15em] " + accent}>
        <CalendarClock className="h-4 w-4" /> {title} ({tasks.length})
      </h2>
      <div className="mt-3 rounded-2xl bg-white border border-border-neutral overflow-hidden">
        {tasks.length === 0 && <p className="p-4 text-sm text-mid-neutral">No tasks here.</p>}
        {tasks.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`}
            className="flex flex-wrap items-center gap-3 p-4 border-b border-border-neutral last:border-0">
            <div className="flex-1 min-w-0">
              <p className={"font-bold truncate " + (t.status === "Open" ? "text-forest" : "text-mid-neutral line-through")}>
                {t.title}
              </p>
              <p className="text-xs text-mid-neutral">
                <Link to={relatedLink(t)} data-testid={`task-related-${t.id}`} className="font-semibold text-forest hover:underline">
                  {t.relatedName}
                </Link>
                {" · "}{t.relatedType}{" · "}{t.ownerName || t.ownerEmail}
              </p>
            </div>
            <TaskDuePill task={t} />
            {t.status === "Open" ? (
              <>
                <button type="button" onClick={() => onStatus(t, "Done")} data-testid={`task-done-${t.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-forest text-white px-2.5 py-1.5 text-xs font-bold">
                  <CheckCircle2 className="h-3 w-3" /> Done
                </button>
                <button type="button" onClick={() => onStatus(t, "Cancelled")} data-testid={`task-cancel-${t.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-soft text-mid-neutral px-2.5 py-1.5 text-xs font-bold">
                  <XCircle className="h-3 w-3" /> Cancel
                </button>
              </>
            ) : (
              <button type="button" onClick={() => onStatus(t, "Open")} data-testid={`task-reopen-${t.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-soft text-forest px-2.5 py-1.5 text-xs font-bold">
                <RotateCcw className="h-3 w-3" /> Reopen
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
